import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { TickerTableDataSource } from './TickerTableDataSource';
import { CollectionAction, CollectionWsMessage } from '../../dto/tickers-dtos';
import { BlastV2Service } from '../../service/blast-v2.service';
import { FieldConfigDTO } from '../../../../../system/system-dtos';
import { LockService } from '../../service/lock-service';

@Component({
  selector: 'app-main-table-content-v2',
  templateUrl: './main-table-content-v2.component.html',
  animations: [
    trigger('updatingIdle', [
      state(
        'idle',
        style({
          opacity: 1,
        })
      ),
      state(
        'updating',
        style({
          opacity: 0.0,
        })
      ),
      transition('idle => updating', [animate('500ms')]),
      transition('updating => idle', [animate('500ms')]),
    ]),
  ],
  styleUrls: ['./main-table-content-v2.component.scss'],
  standalone: false,
})
export class MainTableContentV2Component implements OnInit, OnChanges, OnDestroy {
  private readonly ANIMATION_IN_MILLIS = 500;

  @Input() public dataSource: TickerTableDataSource;

  public loading = true;

  public activeSort: ActiveSort;

  private singleValueChangesSubscription: Subscription;

  private sortSubscription: Subscription;

  private pageDataSubscription: Subscription;

  constructor(
    private blastV2Service: BlastV2Service,
    private lockService: LockService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastrService: ToastrService,
    private domSanitizer: DomSanitizer,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    this.loading = !this.dataSource?.tableConfig || !this.dataSource?.data;
    this.tryToSubscribeToSingleEntryUpdates();
    this.tryToSubscribeToSortChanges();
    this.tryToSubscribeToPageEvent();
  }

  private tryToSubscribeToSortChanges() {
    if (!this.loading && !this.sortSubscription) {
      this.sortSubscription = this.dataSource.sortSubject.subscribe((activeSort) => {
        this.activeSort = activeSort;
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  private tryToSubscribeToSingleEntryUpdates() {
    if (!this.loading && !this.singleValueChangesSubscription) {
      this.singleValueChangesSubscription = this.blastV2Service
        .getSingleValueSubject(`${this.dataSource.tableConfig.code}ticker`)
        .pipe(filter((d) => !!d))
        .subscribe((d) => this.handleSingleValueChange(d));
    }
  }

  private tryToSubscribeToPageEvent() {
    if (!this.loading && !this.pageDataSubscription) {
      this.pageDataSubscription = this.dataSource.pageDataSubject.subscribe((d) => {
        this.ngZone.run(() => this.changeDetectorRef.detectChanges());
      });
    }
  }

  private handleSingleValueChange(d: CollectionWsMessage<any>) {
    switch (d.action) {
      case CollectionAction.ADD:
        this.addElement(d);
        break;
      case CollectionAction.REMOVE:
        this.removeElement(d);
        break;
      case CollectionAction.UPDATE:
        this.updateElement(d);
        break;
    }
  }

  ngOnDestroy(): void {
    if (this.singleValueChangesSubscription) {
      this.singleValueChangesSubscription.unsubscribe();
    }
  }

  getAllFields(): FieldConfigDTO[] {
    return this.dataSource.tableConfig.rows[0].columns[0].fields;
  }

  getTableDisplayFields() {
    return this.getAllFields().filter((f) => f.displayInTable);
  }

  getTableFieldsForExpandedArea() {
    return this.getAllFields().filter((f) => !f.displayInTable);
  }

  private addElement(message: CollectionWsMessage<any>) {
    this.dataSource.addElement(message.data);
    message.data.updating = true;
    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      message.data.updating = false;
      this.changeDetectorRef.detectChanges();
    }, this.ANIMATION_IN_MILLIS);
  }

  private removeElement(message: CollectionWsMessage<any>) {
    const index = this.dataSource.pageData.findIndex((d) => message.data.id === d.data.id);
    if (index >= 0) {
      this.dataSource.pageData[index].updating = true;
      this.changeDetectorRef.detectChanges();
    }
    setTimeout(() => {
      this.dataSource.removeElement(message.data);
      this.changeDetectorRef.detectChanges();
    }, this.ANIMATION_IN_MILLIS);
  }

  private updateElement(message: CollectionWsMessage<any>) {
    const mainIndex = this.dataSource.data.findIndex((d) => message.data.id === d.data.id);
    if (mainIndex === -1) {
      this.addElement(message);
      return;
    }
    message.data.updating = true;
    setTimeout(() => {
      this.dataSource.updateElement(message.data);
      this.changeDetectorRef.detectChanges();
      setTimeout(() => {
        message.data.updating = false;
        this.changeDetectorRef.detectChanges();
      }, this.ANIMATION_IN_MILLIS);
    }, this.ANIMATION_IN_MILLIS);
  }

  onSortChange(field: FieldConfigDTO) {
    if (field.sortable) {
      this.dataSource.sortSubject.next(this.determineActiveSort(field));
    }
  }

  private determineActiveSort(field: FieldConfigDTO): ActiveSort {
    const { activeSort } = this;
    if (field.name === activeSort?.fieldConfig.name && activeSort?.direction === 'ASC') {
      return {
        fieldConfig: field,
        direction: 'DESC',
      };
    }
    if (field.name === activeSort?.fieldConfig.name && activeSort?.direction === 'DESC') {
      return null;
    }
    return {
      fieldConfig: field,
      direction: 'ASC',
    };
  }

  getBackgroundColor(entry: any) {
    if (this.lockService.isEntryLocked(entry)) {
      if (this.lockService.isEntryLockedByCurrentAdmin(entry)) {
        return 'rgba(147, 247, 159, 0.28)';
      }
      return 'rgba(175, 32, 32, 0.15)';
    }
    return '';
  }

  bulkCheckboxClick($event: any) {
    this.dataSource.sendPageDataEvent();
  }

  transform(style: string): SafeStyle {
    if (style === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustStyle(style);
  }

  expandElement(element: any) {
    element.expanded = !element.expanded;
    this.changeDetectorRef.detectChanges();
  }
}

export interface ActiveSort {
  fieldConfig: FieldConfigDTO;
  direction: string;
}
